import DOMPurify from 'dompurify';

export function htmlToMarkdown(html: string): string {
  return String(html || '')
    .replace(/<h1[^>]*>([\s\S]*?)<\/h1>/gi, '# $1\n\n')
    .replace(/<h2[^>]*>([\s\S]*?)<\/h2>/gi, '## $1\n\n')
    .replace(/<h3[^>]*>([\s\S]*?)<\/h3>/gi, '### $1\n\n')
    .replace(/<strong>([\s\S]*?)<\/strong>/gi, '**$1**')
    .replace(/<b>([\s\S]*?)<\/b>/gi, '**$1**')
    .replace(/<em>([\s\S]*?)<\/em>/gi, '*$1*')
    .replace(/<i>([\s\S]*?)<\/i>/gi, '*$1*')
    .replace(/<u>([\s\S]*?)<\/u>/gi, '++$1++')
    .replace(/<li[^>]*>([\s\S]*?)<\/li>/gi, '• $1\n')
    .replace(/<\/ul>/gi, '\n')
    .replace(/<\/ol>/gi, '\n')
    .replace(/<ul[^>]*>/gi, '\n')
    .replace(/<ol[^>]*>/gi, '\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*<p>/gi, '\n\n')
    .replace(/<p[^>]*>/gi, '')
    .replace(/<\/p>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, '\n\n')
    .replace(/[ \t]+\n/g, '\n')
    .trim();
}

export function markdownToEditorHtml(md: string): string {
  if (!md) return '';

  const inlineFmt = (text: string): string =>
    text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\+\+(.+?)\+\+/g, '<u>$1</u>')
      .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>');

  const lines = md.replace(/\r\n/g, '\n').split('\n');
  const parts: string[] = [];
  let inList = false;
  let listTag: 'ul' | 'ol' = 'ul';

  const closeList = () => {
    if (inList) {
      parts.push(`</${listTag}>`);
      inList = false;
    }
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) {
      closeList();
      continue;
    }

    const heading = trimmed.match(/^(#{1,3})\s+(.+)$/);
    if (heading) {
      closeList();
      parts.push(`<h${heading[1].length}>${inlineFmt(heading[2])}</h${heading[1].length}>`);
      continue;
    }

    const bullet = trimmed.match(/^[-•]\s+(.+)$/) || trimmed.match(/^\*\s+(.+)$/);
    if (bullet) {
      if (!inList || listTag !== 'ul') {
        closeList();
        parts.push('<ul>');
        inList = true;
        listTag = 'ul';
      }
      parts.push(`<li><p>${inlineFmt(bullet[1])}</p></li>`);
      continue;
    }

    const numbered = trimmed.match(/^\d+\.\s+(.+)$/);
    if (numbered) {
      if (!inList || listTag !== 'ol') {
        closeList();
        parts.push('<ol>');
        inList = true;
        listTag = 'ol';
      }
      parts.push(`<li><p>${inlineFmt(numbered[1])}</p></li>`);
      continue;
    }

    closeList();
    parts.push(`<p>${inlineFmt(trimmed)}</p>`);
  }

  closeList();
  return DOMPurify.sanitize(parts.join(''));
}

export function editorSourceToHtml(source: string): string {
  const raw = String(source || '').trim();
  if (!raw) return '';
  if (raw.startsWith('<')) return DOMPurify.sanitize(raw);
  return markdownToEditorHtml(raw);
}
